import { useEffect, useState } from 'react'
import type SwiperCore from 'swiper'
import { A11y } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import {
  getYoutubeEmbedUrl,
  getYoutubeThumbnailUrl,
} from '#app/shared/lib/misc.js'
import { Spacer } from '../../data-display/Spacer.tsx'
import { Icon } from '../../icons/Icon.tsx'
import { type GalleryMediaItem } from './StickyFullHeightGallery.tsx'

type Props = {
  mediaItems: GalleryMediaItem[]
  onClickImage: (index: number) => void
  isReserved?: boolean
  isSold?: boolean
}
export const CarouselWithPreview = ({
  mediaItems,
  onClickImage,
  isReserved = false,
  isSold = false,
}: Props) => {
  const [swiperTop, setSwiperTop] = useState<SwiperCore | null>(null)
  const [swiperBottom, setSwiperBottom] = useState<SwiperCore | null>(null)
  const [currentSliderIndex, setCurrentSliderIndex] = useState(0)
  const [videoResetKey, setVideoResetKey] = useState(0)
  const [videoShouldAutoplay, setVideoShouldAutoplay] = useState(false)
  const [videoInlinePlaying, setVideoInlinePlaying] = useState(false)
  const hasVideo = mediaItems[0]?.type === 'video'
  useEffect(() => {
    swiperTop?.slideTo(currentSliderIndex)
    swiperBottom?.slideTo(currentSliderIndex)
  }, [currentSliderIndex])

  useEffect(() => {
    if (currentSliderIndex !== 0) {
      setVideoInlinePlaying(false)
      setVideoShouldAutoplay(false)
    }
  }, [currentSliderIndex])
  return (
    <div>
      <Swiper
        className={`group relative w-full max-w-full`}
        modules={[A11y]}
        slidesPerView={1}
        onSwiper={(swiper) => {
          setSwiperTop(swiper)
        }}
        onSlideChange={(swiper) => {
          setCurrentSliderIndex(swiper.activeIndex)
        }}
      >
        {(isReserved || isSold) && (
          <>
            <div
              className={`
                absolute bottom-0 left-0 right-0 top-0 z-10 rounded-[6px]

                ${isSold ? 'bg-soldOverlay' : 'bg-reservedOverlay'}
              `}
            ></div>
          </>
        )}
        <div className="pointer-events-none absolute top-5 z-20 w-full px-5">
          <div className="flex items-center justify-between">
            {isReserved || isSold ? (
              <div
                className={`
                  pointer-events-auto rounded-md bg-primary-500 px-3 py-1
                  text-sm font-semibold text-white shadow-sm
                `}
              >
                {isSold ? 'Vendido' : 'Reservado'}
              </div>
            ) : (
              <div />
            )}
            <button
              type="button"
              className={`
                pointer-events-auto flex h-[36px] min-h-[36px] w-[36px]
                min-w-[36px] cursor-pointer items-center justify-center
                rounded-full border border-neutral-100 bg-white
              `}
              onClick={() => onClickImage(currentSliderIndex)}
              aria-label="Abrir imagen o vídeo en pantalla completa"
            >
              <Icon
                size="16px"
                name="basic/search"
                className="pointer-events-none text-primary-500"
              />
            </button>
          </div>
        </div>
        <p
          className={`
            absolute bottom-[31px] left-1/2 z-10 -translate-x-[50%] text-[14px]
            font-bold leading-4 text-white
          `}
        >
          <span>{currentSliderIndex + 1}</span>/<span>{mediaItems.length}</span>
        </p>
        {hasVideo && currentSliderIndex !== 0 && (
          <button
            type="button"
            onClick={() => {
              setCurrentSliderIndex(0)
              setVideoInlinePlaying(true)
              setVideoShouldAutoplay(true)
              setVideoResetKey((k) => k + 1)
            }}
            className={`
              absolute bottom-[26px] right-5 z-10 flex cursor-pointer
              items-center gap-2 rounded-md bg-black/60 px-3 py-2 text-sm
              font-semibold text-white

              hover:bg-black/80
            `}
          >
            Vídeo
          </button>
        )}
        <button
          type="button"
          className={`
            absolute left-5 top-1/2 z-10 flex h-[36px] min-h-[36px] w-[36px]
            min-w-[36px] -translate-y-[50%] items-center justify-center
            rounded-full border border-neutral-100

            ${
              currentSliderIndex < 1
                ? `cursor-not-allowed bg-[rgba(1,1,1,0.25)]`
                : `cursor-pointer bg-white`
            }
          `}
          onClick={() => {
            if (currentSliderIndex < 1) return
            setCurrentSliderIndex((init) => init - 1)
          }}
          disabled={currentSliderIndex < 1}
        >
          <Icon
            size="12px"
            name="basic/chevron-left"
            className={`
              pointer-events-none

              ${currentSliderIndex <= 0 ? `text-purple-300` : `text-neutral-300`}
            `}
          />
        </button>

        {/* Right Arrow Button */}
        <button
          type="button"
          className={`
            absolute right-5 top-1/2 z-10 flex h-[36px] min-h-[36px] w-[36px]
            min-w-[36px] -translate-y-[50%] cursor-pointer items-center
            justify-center rounded-full border border-neutral-100

            lg:opacity-0 lg:group-hover:opacity-100

            ${
              currentSliderIndex > mediaItems.length - 2
                ? `cursor-not-allowed bg-[rgba(1,1,1,0.25)]`
                : `cursor-pointer bg-white`
            }
          `}
          disabled={currentSliderIndex > mediaItems.length - 2}
          onClick={() => {
            if (currentSliderIndex > mediaItems.length - 2) return
            setCurrentSliderIndex((init) => init + 1)
          }}
        >
          <Icon
            size="12px"
            name="basic/chevron-right"
            className={`
              pointer-events-none

              ${
                currentSliderIndex >= mediaItems.length - 1
                  ? 'text-purple-300'
                  : `text-neutral-300`
              }
            `}
          />
        </button>

        {mediaItems.map((item, index) => (
          <SwiperSlide key={index}>
            {item.type === 'video' ? (
              <div
                className={`
                  relative aspect-[4/3] w-full overflow-hidden rounded-[6px]
                  bg-neutral-900
                `}
              >
                {videoInlinePlaying ? (
                  <iframe
                    key={`video-${videoResetKey}`}
                    title="Vídeo del coche"
                    src={
                      getYoutubeEmbedUrl(item.url, {
                        muted: true,
                        autoplay:
                          currentSliderIndex === 0 && videoShouldAutoplay,
                        start: 0,
                      }) ?? ''
                    }
                    className="h-full w-full rounded-[6px]"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <>
                    <img
                      src={
                        item.previewImageUrl ??
                        getYoutubeThumbnailUrl(item.url, 'maxresdefault') ??
                        getYoutubeThumbnailUrl(item.url) ??
                        ''
                      }
                      alt="Portada del vídeo"
                      className="h-full w-full object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-black/35" />
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentSliderIndex(0)
                        setVideoInlinePlaying(true)
                        setVideoShouldAutoplay(true)
                        setVideoResetKey((k) => k + 1)
                      }}
                      className={`
                        absolute left-1/2 top-1/2 z-10 flex h-[56px] w-[56px]
                        -translate-x-1/2 -translate-y-1/2 items-center
                        justify-center rounded-full bg-black/60 text-white
                        shadow-lg backdrop-blur-[1px] transition-colors

                        hover:bg-black/75
                      `}
                      aria-label="Reproducir vídeo"
                    >
                      <svg
                        className="h-9 w-9"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M5 4.623V19.38a1.5 1.5 0 002.26 1.29L22 12 7.26 3.33A1.5 1.5 0 005 4.623Z" />
                      </svg>
                    </button>
                  </>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => onClickImage(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onClickImage(index)
                  }
                }}
                className={`
                  w-full

                  lg:rounded-[6px]
                `}
              >
                <img
                  src={item.url}
                  alt="outstanding"
                  loading="lazy"
                  className={`
                    aspect-[4/3] h-full w-full rounded-[6px] object-cover
                    object-center
                  `}
                />
              </button>
            )}
          </SwiperSlide>
        ))}
      </Swiper>
      <Spacer size="16px" />

      <Swiper
        className={`
          relative overflow-hidden

          lg:max-w-[888px] lg:!pl-0

          md:!pl-7
        `}
        modules={[A11y]}
        slidesPerView={3}
        breakpoints={{
          450: {
            slidesPerView: 4.6,
            spaceBetween: 12,
          },
          600: {
            slidesPerView: 5,
            spaceBetween: 12,
          },
          768: {
            slidesPerView: 5.6,
            spaceBetween: 12,
          },
          1150: {
            slidesPerView: 7,
            spaceBetween: 4,
          },
        }}
        spaceBetween={'4px'}
        onSwiper={(swiper) => {
          setSwiperBottom(swiper)
        }}
      >
        {mediaItems.map((item, index) => (
          <SwiperSlide
            className="relative overflow-clip"
            key={index}
            onClick={() => setCurrentSliderIndex(index)}
          >
            {item.type === 'video' ? (
              <div
                className={`
                  relative h-[80px] w-full max-w-[120px] overflow-hidden rounded

                  ${
                    currentSliderIndex === index
                      ? `border border-[#000] opacity-90`
                      : ''
                  }
                `}
              >
                <img
                  src={
                    item.previewImageUrl ??
                    getYoutubeThumbnailUrl(item.url, 'maxresdefault') ??
                    getYoutubeThumbnailUrl(item.url) ??
                    ''
                  }
                  alt="Vídeo"
                  className="h-full w-full object-cover object-center"
                />
                <div
                  className={`
                    absolute inset-0 flex items-center justify-center
                    bg-black/20
                  `}
                >
                  <div
                    className={`
                      flex h-7 w-7 items-center justify-center rounded-full
                      bg-black/55 text-white shadow-md
                    `}
                  >
                    <svg
                      className="h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M5 4.623V19.38a1.5 1.5 0 002.26 1.29L22 12 7.26 3.33A1.5 1.5 0 005 4.623Z" />
                    </svg>
                  </div>
                </div>
              </div>
            ) : (
              <img
                src={item.url}
                alt="thumbnail"
                loading="lazy"
                className={`
                  h-[80px] w-full max-w-[120px] rounded object-cover
                  object-center

                  ${
                    currentSliderIndex === index
                      ? `border border-[#000] opacity-20`
                      : ''
                  }
                `}
              />
            )}
            <div
              className={`
                absolute left-0 top-0 z-[1] h-[4px] w-[120px] rounded-t

                ${currentSliderIndex === index ? `opacity-100` : 'opacity-0'}

                bg-primary-500
              `}
            />
          </SwiperSlide>
        ))}
        <button
          type="button"
          className={`
            absolute left-5 top-1/2 z-10 flex h-[36px] min-h-[36px] w-[36px]
            min-w-[36px] -translate-y-[50%] cursor-pointer items-center
            justify-center rounded-full border border-neutral-100 bg-white

            xl:hidden

            ${currentSliderIndex < 1 ? 'opacity-0' : `opacity-100`}
          `}
          disabled={currentSliderIndex < 1}
          onClick={() => {
            if (currentSliderIndex < 1) return
            setCurrentSliderIndex((init) => init - 1)
          }}
        >
          <Icon
            size="12px"
            name="basic/chevron-left"
            className={`
              pointer-events-none

              ${
                currentSliderIndex >= mediaItems.length - 1
                  ? 'text-purple-300'
                  : `text-neutral-300`
              }
            `}
          />
        </button>
        <button
          type="button"
          className={`
            absolute right-5 top-1/2 z-10 flex h-[36px] min-h-[36px] w-[36px]
            min-w-[36px] -translate-y-[50%] cursor-pointer items-center
            justify-center rounded-full border border-neutral-100 bg-white

            xl:hidden

            ${
              currentSliderIndex > mediaItems.length - 2
                ? 'opacity-0'
                : `opacity-100`
            }
          `}
          disabled={currentSliderIndex > mediaItems.length - 2}
          onClick={() => {
            if (currentSliderIndex > mediaItems.length - 2) return
            setCurrentSliderIndex((init) => init + 1)
          }}
        >
          <Icon
            size="12px"
            name="basic/chevron-right"
            className={`
              pointer-events-none

              ${
                currentSliderIndex >= mediaItems.length - 1
                  ? 'text-purple-300'
                  : `text-neutral-300`
              }
            `}
          />
        </button>
      </Swiper>
    </div>
  )
}
